import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface ProjectDto {
  id: number;
  isNewProject?: boolean;
  estimateNumber?: string;
  sequenceNumber: string | number;
  estimateYear: number;
  projectTitle: string;
  estimateTitle: string;
  projectStatusType: number;
  createdDate: string;
  submittedDate?: string | null;
  tcoEstimator?: string | null;
  // Detail fields (from /api/Projects/{id})
  projectNumber?: string;
  projectCode?: string;
  estimateRequestType?: string | null;
  projectManagementType?: string;
  cpdepPhase?: string | null;
  designProgress?: string | null;
  projectPhase?: string | null;
  classOfEstimate?: string | null;
  classNumber?: string | null;
  projectCompletionDate?: string;
  deadlineDate?: string;
  area?: string;
  unit?: string;
  ibpYear?: string | null;
  turnaroundYear?: string;
  status?: string;
  requestor?: string;
  projectTeam?: string;
  afeNumber?: string;
}

export interface PaginatedResponse<T> {
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
  totalRecords: number;
  items: T[];
}

export interface CreateProjectCommand {
  estimateYear?: number;
  sequenceNumber?: string;
  projectTeam: string;
  afeNumber: string;
  projectTitle: string;
  estimateTitle: string;
  projectNumber: string;
  projectCode: string;
  estimateRequestTypeId: number;
  projectManagementTypeId: number;
  cpdepPhaseId: number;
  designProgressId: number;
  projectCompletionDate: string;
  deadlineDate: string;
  areaId: number;
  unitId: number;
  ibpYearId: number;
  turnaroundYearId: number;
}

export interface UpdateProjectCommand {
  projectTeam: string;
  afeNumber: string;
  projectTitle: string;
  estimateTitle: string;
  projectNumber: string;
  projectCode: string;
  estimateRequestTypeId: number;
  projectManagementTypeId: number;
  cpdepPhaseId: number;
  designProgressId: number;
  projectCompletionDate: string;
  deadlineDate: string;
  areaId: number;
  unitId: number;
  ibpYearId: number;
  turnaroundYearId: number;
}

export interface SubmitProjectCommand extends UpdateProjectCommand {
  id: number;
  projectTeam: string;
  afeNumber: string;
}

export interface ProjectCustomDropdownDto {
  projectTitle: string | null;
  estimateYear: number | null;
  projectNumber: string | null;
  estimateTitle: string | null;
  projectCode: string | null;
}

export interface HistoricalProjectDto {
  estimateNumber: string;
  projectTitle: string;
  submittedDate?: string | null;
  classOfEstimate: string;
  linkToAnEstimate: string;
  projectNumber: string;
  projectCode: string;
  turnaroundYear: string;
  locationFacility: string;
  locationUnit: string;
  equipmentName: string;
  estimateTitle: string;
  afeNumber: string;
  supplementNumber: string;
  projectCompletionDate: string;
  taspdst: string;
  dateReceived: string;
  deadlineDate: string;
  startDate: string;
  dateCompleted: string;
  requestor: string;
  departmentCostCenter: string;
  departmentGroupL1: string;
  departmentGroupL2: string;
  departmentGroupL3: string;
  tcoEstimator: string;
  tcoSecondaryEstimator: string;
  tcoTertiaryEstimator: string;
  tcoQuaternaryEstimator: string;
  tcoReviewer: string;
  sentTo: string;
  contingencyCalculationMethod: string;
  status: string;
  projectStatusType: number;
  agileMethodology: string;
  comments: string;
  engUsd: number;
  engKzt: number;
  engEur: number;
  engGbp: number;
  procUsd: number;
  procKzt: number;
  procEur: number;
  procGbp: number;
  constUsd: number;
  constKzt: number;
  constEur: number;
  constGbp: number;
  ownUsd: number;
  ownKzt: number;
  ownEur: number;
  ownGbp: number;
  cntgUsd: number;
}

export interface RequestedProjectDto {
  id: number;
  estimateNumber: string | null;
  projectTitle: string | null;
  estimateTitle?: string | null;
  ceRequestDate?: string | null;
  createdDate: string | null;
  submittedDate?: string | null;
  requestor?: string | null;
  status: string | null;
  assignedCe: string | null;
  projectStatusType: number;
}

export type HistoricalProjectsFilterQuery = Record<string, readonly (string | number)[] | undefined>;
export type ProjectsFilterQuery = Record<string, readonly (string | number)[] | undefined>;

export interface CostEstimatorDto {
  objectId: string;
  fullName: string;
}

export interface ProjectEngineerDto {
  objectId: string;
  fullName: string;
}

export interface B2bUserDto {
  id: number;
  userId: string | null;
  backToBackId: string | null;
  partnerName: string | null;
  isRequester: boolean;
  statusType: number;
}

export interface AddB2bUserCommand {
  backToBackId: string;
}

export interface UpdateProjectCeCommand {
  id: number;
  ceEngineerObjectId: string;
  tcoEstimator: string;
}

export interface ProjectResponseCommentDto {
  id: number;
  projectId: number;
  text: string;
  createdDate: string;
  createdBy: string;
}

export interface ProjectResponseDto {
  id: number;
  tcoEstimator: string;
  tcoSecondaryEstimator: string;
  tcoReviewer: string;
  projectStatusType?: number;
  status?: string;
  dateCompleted: string;
  alternativeNumber: string;
  type: string;
  revision: string;
  contingencyCalculationMethod: string;
  comments: string;
  commentHistory: ProjectResponseCommentDto[];
}

export interface ProjectAttachmentDto {
  id: number;
  projectId: number;
  entityType: number;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  createdDate: string;
  createdBy: string;
}

export interface UpdateProjectResponseCommand {
  id: number;
  projectStatusType: number;
  tcoEstimator?: string;
  tcoSecondaryEstimator?: string;
  tcoReviewer?: string;
  dateCompleted?: string | null;
  alternativeNumber?: string;
  type?: string;
  revision?: string;
  contingencyCalculationMethod?: string;
  comments?: string;
}

@Injectable({ providedIn: 'root' })
export class ProjectsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  getProjects(
    pageIndex: number = 1,
    pageSize: number = 10,
    filters?: ProjectsFilterQuery,
  ): Observable<PaginatedResponse<ProjectDto>> {
    let params = new HttpParams()
      .set('PageIndex', String(pageIndex))
      .set('PageSize', String(pageSize));

    if (filters) {
      for (const [key, values] of Object.entries(filters)) {
        if (!values?.length) {
          continue;
        }

        for (const value of values) {
          params = params.append(key, String(value));
        }
      }
    }

    return this.http.get<PaginatedResponse<ProjectDto>>(
      `${this.apiBaseUrl}/api/Projects`,
      { params },
    );
  }

  getRequestedProjects(
    pageIndex: number = 1,
    pageSize: number = 10,
    filters?: ProjectsFilterQuery,
  ): Observable<PaginatedResponse<RequestedProjectDto>> {
    let params = new HttpParams()
      .set('Pagination.PageIndex', String(pageIndex))
      .set('Pagination.PageSize', String(pageSize));

    if (filters) {
      for (const [key, values] of Object.entries(filters)) {
        if (!values?.length) {
          continue;
        }

        for (const value of values) {
          params = params.append(key, String(value));
        }
      }
    }

    return this.http.get<PaginatedResponse<RequestedProjectDto>>(
      `${this.apiBaseUrl}/api/Projects/requested`,
      { params },
    );
  }

  getHistoricalProjects(
    pageIndex: number = 1,
    pageSize: number = 10,
    filters?: HistoricalProjectsFilterQuery,
  ): Observable<PaginatedResponse<HistoricalProjectDto>> {
    let params = new HttpParams()
      .set('Pagination.PageIndex', String(pageIndex))
      .set('Pagination.PageSize', String(pageSize));

    if (filters) {
      for (const [key, values] of Object.entries(filters)) {
        if (!values?.length) {
          continue;
        }

        for (const value of values) {
          params = params.append(key, String(value));
        }
      }
    }

    return this.http.get<PaginatedResponse<HistoricalProjectDto>>(
      `${this.apiBaseUrl}/api/Projects/historical`,
      { params },
    );
  }

  exportHistoricalProjects(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiBaseUrl}/api/Projects/historical/export`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getProjectById(id: number): Observable<ProjectDto> {
    return this.http.get<ProjectDto>(`${this.apiBaseUrl}/api/Projects/${id}`);
  }

  getProjectResponse(id: number): Observable<ProjectResponseDto> {
    return this.http.get<ProjectResponseDto>(`${this.apiBaseUrl}/api/Projects/${id}/response`);
  }

  updateProjectResponse(id: number, command: UpdateProjectResponseCommand): Observable<number> {
    return this.http.put<number>(`${this.apiBaseUrl}/api/Projects/${id}/response`, command);
  }

  getProjectAttachments(projectId: number, entityType: number): Observable<ProjectAttachmentDto[]> {
    const params = new HttpParams().set('entityType', String(entityType));
    return this.http.get<ProjectAttachmentDto[]>(`${this.apiBaseUrl}/api/Projects/${projectId}/attachments`, { params });
  }

  uploadProjectAttachments(projectId: number, files: File[], entityType: number): Observable<ProjectAttachmentDto[]> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file, file.name);
    }

    const params = new HttpParams().set('entityType', String(entityType));
    return this.http.post<ProjectAttachmentDto[]>(`${this.apiBaseUrl}/api/Projects/${projectId}/attachments`, formData, { params });
  }

  downloadProjectAttachment(projectId: number, attachmentId: number, entityType: number): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('entityType', String(entityType));
    return this.http.get(`${this.apiBaseUrl}/api/Projects/${projectId}/attachments/${attachmentId}/download`, {
      observe: 'response',
      responseType: 'blob',
      params,
    });
  }

  deleteProjectAttachment(projectId: number, attachmentId: number, entityType: number, options?: { skipNotification?: boolean }): Observable<void> {
    let params = new HttpParams().set('entityType', String(entityType));
    const headers = options?.skipNotification ? { 'X-Skip-Notification': '1' } : undefined;
    return this.http.delete<void>(`${this.apiBaseUrl}/api/Projects/${projectId}/attachments/${attachmentId}`, { params, headers });
  }

  createNewProject(command: CreateProjectCommand): Observable<ProjectDto> {
    return this.http.post<ProjectDto>(`${this.apiBaseUrl}/api/Projects/new`, command);
  }

  createExistingProject(command: CreateProjectCommand): Observable<ProjectDto> {
    return this.http.post<ProjectDto>(`${this.apiBaseUrl}/api/Projects/existing`, command);
  }

  updateProject(id: number, command: UpdateProjectCommand): Observable<number> {
    return this.http.put<number>(`${this.apiBaseUrl}/api/Projects/${id}`, command);
  }

  submitProject(id: number, command: SubmitProjectCommand): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/api/Projects/${id}/submit`, command);
  }

  searchProject(estimateYear: number, sequenceNumber: string | number): Observable<ProjectDto> {
    return this.http.get<ProjectDto>(
      `${this.apiBaseUrl}/api/Projects/${estimateYear}/${sequenceNumber}`,
    );
  }

  getCustomDropdownProjects(estimateYear: number, sequenceNumber: string): Observable<ProjectCustomDropdownDto[]> {
    return this.http.get<ProjectCustomDropdownDto[]>(
      `${this.apiBaseUrl}/api/Projects/customDropdowns?sequenceNumber=${encodeURIComponent(sequenceNumber)}&estimateYear=${estimateYear}`,
    );
  }

  getCostEstimators(): Observable<CostEstimatorDto[]> {
    return this.http.get<CostEstimatorDto[]>(`${this.apiBaseUrl}/api/Users/costEstimators`);
  }

  getProjectEngineers(): Observable<ProjectEngineerDto[]> {
    return this.http.get<ProjectEngineerDto[]>(`${this.apiBaseUrl}/api/Users/ProjectEngineers`);
  }

  getB2bUsers(): Observable<B2bUserDto[]> {
    return this.http.get<B2bUserDto[]>(`${this.apiBaseUrl}/api/Users/b2b`);
  }

  addB2bUser(command: AddB2bUserCommand): Observable<number> {
    return this.http.post<number>(`${this.apiBaseUrl}/api/Users/b2b`, command);
  }

  acceptB2bUser(id: number): Observable<number> {
    return this.http.put<number>(`${this.apiBaseUrl}/api/Users/b2b/${id}/accept`, {});
  }

  deleteB2bUser(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/api/Users/b2b/${id}`);
  }


  updateProjectCe(id: number, command: UpdateProjectCeCommand): Observable<number> {
    return this.http.put<number>(`${this.apiBaseUrl}/api/Projects/${id}/ce`, command, {
      withCredentials: true,
    });
  }

}

